import { useAppDispatch, useAppSelector } from 'hooks';
import { Typography } from 'interfaces/typography';
import React, { useState } from 'react';
import { getServicesManager } from 'services';
import {
  updateSessionStatus,  
  selectCount,
} from './SessionStoreSetter';
import './hover.css'
import { updatePendingReviews } from 'lib/redux/sessions/lists/list-slice';
import { convertFromReduxSafeUserState } from 'lib/redux/users/slice';
import { RootState } from 'store';
import { connect } from 'react-redux';
import { UserDataResult } from "interfaces/users";
import { CAPITAL_GENERATIONS_TEAM_ID } from 'cfg/const';

const unfilledFlag = 
<svg className='flag' viewBox="0 0 737 747" fill="none" xmlns="http://www.w3.org/2000/svg">
<path className='flag' fillRule="evenodd" clipRule="evenodd" d="M63.2045 466.659V715.685C63.2045 732.795 49.3925 746.664 32.0898 746.664C14.9071 746.664 0.980469 732.695 0.980469 715.685V30.9787C0.980469 13.8693 14.9018 0 32.1111 0H716.511C733.705 0 741.126 12.2188 733 27.4533L637.839 205.88C629.751 221.041 629.714 245.552 637.839 260.787L733 439.213C741.084 454.375 733.719 466.667 716.511 466.667L63.2045 466.659ZM63.2045 62.2187V404.432H643.938L582.938 290.057C565.058 256.536 565.083 210.068 582.938 176.588L643.938 62.2133L63.2045 62.2187Z" fill="currentColor"/>
</svg>

const filledFlag = 
<svg className='flag' viewBox="0 0 736 747" fill="none" xmlns="http://www.w3.org/2000/svg">
<path className='flag' d="M62.224 466.659V715.685C62.224 732.795 48.412 746.664 31.1093 746.664C13.9267 746.664 0 732.695 0 715.685V30.9787C0 13.8693 13.9213 0 31.1307 0H715.531C732.724 0 740.145 12.2188 732.02 27.4533L636.859 205.88C628.77 221.041 628.734 245.552 636.859 260.787L732.02 439.213C740.103 454.375 732.739 466.667 715.531 466.667L62.224 466.659Z" fill="currentColor"/>
<path className='flag' d="M62.224 62.2187V404.432H642.957L581.957 290.057C564.077 256.536 564.103 210.068 581.957 176.588L642.957 62.2133L62.224 62.2187Z" fill="currentColor"/>
</svg>

export type SessionStatusProps = {
  type: 'sessionData' | 'sessionList'
  sessionId: string
  sessionUserId: string
  viewerUserId: string
  reviewIsOpen?: boolean | null
  reviewOpenedByUser?: boolean 
  isMainView?: boolean
  pendingReviews: number | null
  user: UserDataResult | null
}

function SessionStatusImpl(props: SessionStatusProps) {
  const count = useAppSelector(selectCount);
  const dispatch = useAppDispatch();

  function _handleReview(): void {
    const sessionInfo = count[props.sessionId]
    const hasSession = sessionInfo !== undefined
    const reviewState =  hasSession ? !sessionInfo.reviewIsOpen : (props.reviewIsOpen ? false : true)
    getServicesManager().updateSessionReview(props.sessionId, reviewState).then((x) => {
      if (x !== null) {
        dispatch(updateSessionStatus({
        'sessionId': props.sessionId, 'reviewIsOpen': reviewState, 
        'reviewOpenedByUser': reviewState ? true : undefined, 'star': false }))
        if (props.pendingReviews !== null) {
          dispatch(updatePendingReviews({
            'value': props.pendingReviews + (reviewState ? 1 : -1)
          }))
        }
    }
  })
  }
  const reviewIsOpen = count[props.sessionId] ? count[props.sessionId].reviewIsOpen : props.reviewIsOpen
  const reviewOpenedByUser = count[props.sessionId] ? count[props.sessionId].reviewOpenedByUser : props.reviewOpenedByUser

  function showReviewIcon(): boolean {
    return (props.reviewIsOpen !== undefined && props.reviewIsOpen !== null) || props.sessionUserId !== props.viewerUserId || [CAPITAL_GENERATIONS_TEAM_ID].includes(props.user?.team_id ?? '')
  }
  if (!showReviewIcon()) return null
  return (
    <div className='flex flex-row items-center justify-center gap-1'>
        <DebouncerIcon isMainView={props.isMainView} reviewIsOpen={reviewIsOpen} reviewOpenedByUser={reviewOpenedByUser} type={props.type} updateReview={_handleReview}/>
    </div>
  );
}

export type DebouncerIconProps = {
  type: 'sessionData' | 'sessionList'
  reviewIsOpen: boolean | undefined | null,
  reviewOpenedByUser: boolean | undefined, 
  isMainView?: boolean
  updateReview: () => void
}

export type DebouncerIconState = {
  showSuggestion: boolean, 
  timeout: NodeJS.Timeout | null
}

class DebouncerIcon extends React.Component<DebouncerIconProps, DebouncerIconState> {
  constructor(props: DebouncerIconProps) {
    super(props)
    this.state = {
      'showSuggestion': false,
      'timeout': null
    }
  }

  renderFlagIcon(): JSX.Element {
    const isSessionData = this.props.type === 'sessionData';
    const baseIconSize = isSessionData ? '16px' : '12px';
    const baseIconStyles = isSessionData 
      ? {
          width: baseIconSize,
          height: baseIconSize,
        }
      : {
          width: baseIconSize,
          height: baseIconSize,
          marginTop: '2px'
        };

    return (
      <div>
        {this.props.reviewIsOpen || (this.props.reviewIsOpen === undefined || this.props.reviewIsOpen === null) ? (
          <div 
            onClick={this.props.updateReview} 
            className={
              this.props.reviewIsOpen 
                ? `cursor-pointer ${isSessionData ? 'text-red-500 hover:text-red-600' : 'text-black'} flex items-center justify-center` 
                : `cursor-pointer ${isSessionData ? 'text-gray-400 hover:text-gray-600' : 'text-black'} flex items-center justify-center`
            } 
            style={baseIconStyles}
          >
            {this.props.reviewIsOpen ? filledFlag : unfilledFlag}
          </div>
        ) : (
          <div 
            onClick={this.props.updateReview} 
            className={`cursor-pointer items-center flex justify-center ${isSessionData ? 'text-green-500 hover:text-green-600' : 'text-green-600'}`}
            style={baseIconStyles}
          >
            &#x2713;
          </div>
        )}
      </div>
    )
  }

  render(): React.ReactNode {
    const isSessionData = this.props.type === 'sessionData';
    
    return (
      <div className={`flex items-center justify-center ${isSessionData ? 'p-1.5 rounded-full hover:bg-gray-100 transition-colors' : ''}`}>
        {this.renderFlagIcon()}
      </div>
    )
  }
}

const mapStateToProps = (state: RootState) => {
  return {
    pendingReviews: state.pendingReviews.value,
    user: convertFromReduxSafeUserState(state.user)
  };
};

const ReduxWrapped = connect(mapStateToProps)(SessionStatusImpl)

export { ReduxWrapped as SessionStatus}